/**
 * `/leads/inbox` — Inbox view: leads sorted by most recent message activity.
 *
 * Cursor-paginated via the computed activity timestamp (last_message.sent_at
 * COALESCE created_at). Page 1 polls every 15s for the live-tail feel;
 * subsequent pages are static.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLeadsInbox } from "@/hooks/use-leads";
import type { InboxLeadResponse } from "@/lib/api-client";
import { formatTimestamp } from "@/lib/formatters";

const PAGE_LIMIT = 50;

const STATUS_COLORS: Record<InboxLeadResponse["status"], string> = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  qualified: "bg-purple-100 text-purple-800",
  booked: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
  lost: "bg-red-100 text-red-700",
};

export function LeadsInboxPage() {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allItems, setAllItems] = useState<InboxLeadResponse[]>([]);
  const isLoadMoreRef = useRef(false);

  const inbox = useLeadsInbox({
    limit: PAGE_LIMIT,
    ...(cursor !== undefined && { cursor }),
  });

  useEffect(() => {
    if (!inbox.data) return;
    if (isLoadMoreRef.current) {
      setAllItems((prev) => [...prev, ...(inbox.data?.items ?? [])]);
      isLoadMoreRef.current = false;
    } else {
      // Page 1 polls every 15s; replace items so live updates land.
      setAllItems(inbox.data.items);
    }
  }, [inbox.data]);

  function handleLoadMore() {
    if (!inbox.data?.next_cursor) return;
    isLoadMoreRef.current = true;
    setCursor(inbox.data.next_cursor);
  }

  const isInitialLoading = inbox.isLoading && allItems.length === 0;
  const isFetchingMore = inbox.isFetching && allItems.length > 0 && cursor !== undefined;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Leads</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Inbox</h1>
        <p className="text-sm text-muted-foreground">
          Leads sorted by most recent message activity.
        </p>
      </header>

      {isInitialLoading && (
        <div className="flex flex-col gap-2" data-testid="inbox-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      )}

      {inbox.isError && (
        <p className="text-sm text-destructive">Failed to load inbox.</p>
      )}

      {!isInitialLoading && allItems.length === 0 && !inbox.isError && (
        <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
          <p className="text-sm text-muted-foreground">No leads with messages yet.</p>
          <Link to="/leads" className="text-sm text-primary hover:underline">
            View all leads →
          </Link>
        </div>
      )}

      {allItems.length > 0 && (
        <>
          <div className="flex flex-col gap-2" data-testid="inbox-list">
            {allItems.map((lead) => (
              <Link key={lead.id} to={`/leads/${lead.id}`}>
                <Card className="border-gray-200 hover:border-primary/40 transition-colors">
                  <CardContent className="flex items-start justify-between gap-3 p-4">
                    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {lead.source ?? "Direct"} — #{lead.id.slice(0, 8)}
                        </span>
                        <span
                          className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[lead.status]}`}
                        >
                          {lead.status}
                        </span>
                      </div>
                      {lead.last_message_body ? (
                        <p className="text-xs text-muted-foreground truncate max-w-md">
                          {lead.last_message_body}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No messages yet</p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs text-muted-foreground">
                        {lead.last_message_at
                          ? formatTimestamp(lead.last_message_at)
                          : formatTimestamp(lead.created_at)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {inbox.data?.next_cursor && (
            <div className="mt-2 flex justify-center">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isFetchingMore}
                data-testid="inbox-load-more"
              >
                {isFetchingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default LeadsInboxPage;
